import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sheet } from '../../../components/ui/Sheet';
import { Button } from '../../../components/ui/Button';
import { DESIGNATION_COLORS, DESIGNATION_SHORT } from '../designation';
import { formatIst as formatTime } from '../formatIst';
import { useResearchSchedule } from '../hooks/useResearchSchedule';
import type {
  ResearchRunStatus,
  ResearchScheduleBucket,
  TargetAskScheduleBucket,
  ResearchScheduleOverview,
} from '../../../services/dashboard.service';

const FREQUENCY_LABELS: Record<number, [string, string]> = {
  7: ['1w', 'Every week'],
  14: ['2w', 'Every 2 weeks'],
  30: ['1m', 'Every month'],
  60: ['2m', 'Every 2 months'],
};


const hourLabel = (h: number) =>
  h === 0 ? '12 am' : h < 12 ? `${h} am` : h === 12 ? '12 pm' : `${h - 12} pm`;

const STATUS_STYLES: Record<ResearchRunStatus, string> = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
  skipped: 'bg-slate-50 text-slate-600 border-slate-200',
};

function StatusChip({ status }: { status: ResearchRunStatus }) {
  return (
    <span
      className={`px-1.5 py-0.5 rounded-md border text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label className="relative inline-flex items-center cursor-pointer" title={label}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
        aria-label={label}
      />
      <div className="w-8 h-4 bg-slate-200 peer-checked:bg-accent rounded-full transition-colors peer-disabled:opacity-50" />
      <div className="absolute left-0.5 top-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
    </label>
  );
}

function CapInput({
  cap,
  saving,
  onUpdate,
}: {
  cap: number;
  saving: boolean;
  onUpdate: ReturnType<typeof useResearchSchedule>['update'];
}) {
  const [draft, setDraft] = useState(String(cap));
  const changed = draft.trim() !== '' && Number(draft) !== cap;
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={0}
        max={500}
        step={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        aria-label="Profiles per night"
        className="w-20 bg-white border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 outline-none rounded-lg px-2.5 py-1.5 text-xs text-slate-800 font-medium"
      />
      {changed && (
        <Button
          variant="accent"
          className="!px-3 !py-1.5 !text-[11px]"
          isLoading={saving}
          onClick={() => onUpdate({ nightly_cap: Number(draft) }, `Up to ${draft} profiles per night.`)}
        >
          Save
        </Button>
      )}
    </div>
  );
}

function StatusCard({
  overview,
  saving,
  onUpdate,
}: {
  overview: ResearchScheduleOverview;
  saving: boolean;
  onUpdate: ReturnType<typeof useResearchSchedule>['update'];
}) {
  const { runner, window: win } = overview;
  const anyEnabled = overview.buckets.some((b) => b.enabled);

  return (
    <div className="rounded-2xl border border-slate-200/70 bg-slate-50/60 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-800">
            {overview.paused ? 'Paused' : 'Running'}
          </p>
          <p className="text-[10px] text-slate-400">
            {overview.paused
              ? 'No bucket is researched until you resume.'
              : 'Enabled buckets are researched every night.'}
          </p>
        </div>
        <Toggle
          checked={!overview.paused}
          disabled={saving}
          label={overview.paused ? 'Resume scheduled research' : 'Pause scheduled research'}
          onChange={(on) =>
            onUpdate({ paused: !on }, on ? 'Scheduled research resumed.' : 'Scheduled research paused.')
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-3 text-[11px]">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Window</p>
          <p className="text-slate-700 font-medium">
            {hourLabel(win.start_hour)} – {hourLabel(win.end_hour)} {win.timezone}
          </p>
          <p className="text-[10px] text-slate-400">{overview.in_window ? 'Open now' : 'Closed now'}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Tonight</p>
          <p className="text-slate-700 font-medium">
            {runner.done_tonight} / {overview.nightly_cap} profiles
          </p>
          <p className="text-[10px] text-slate-400">
            Last run {formatTime(runner.last_run_at)}
          </p>
        </div>
      </div>

      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
          Profiles per night
        </p>
        {/* Keyed on the saved cap so the draft resets whenever it changes. */}
        <CapInput key={overview.nightly_cap} cap={overview.nightly_cap} saving={saving} onUpdate={onUpdate} />
        
      </div>

      {anyEnabled && (
        <p className="text-[10px] text-slate-500 leading-relaxed">
          {overview.cycle_nights === null
            ? 'At 0 per night nothing will run.'
            : `At ${overview.nightly_cap} per night, researching every enabled alumnus once takes ≈ ${overview.cycle_nights.toLocaleString('en-IN')} night${overview.cycle_nights === 1 ? '' : 's'}. The most overdue go first.`}
        </p>
      )}

      {runner.tripped && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
          <p className="text-[11px] font-semibold text-rose-700">
            Stopped for tonight after {runner.consecutive_failures} failures in a row
          </p>
          <p className="text-[10px] text-rose-600 mt-0.5">
            It tries again tomorrow night. Usually this means Parallel credits ran out or a
            provider was down.
          </p>
          {runner.last_error && (
            <p className="text-[10px] text-rose-500 mt-1 font-mono break-words">{runner.last_error}</p>
          )}
        </div>
      )}
    </div>
  );
}

function BucketRow({
  bucket,
  cycleNights,
  cap,
  saving,
  onUpdate,
}: {
  bucket: ResearchScheduleBucket;
  cycleNights: number | null;
  cap: number;
  saving: boolean;
  onUpdate: ReturnType<typeof useResearchSchedule>['update'];
}) {
  const short = DESIGNATION_SHORT[bucket.category] ?? bucket.category;
  const save = (enabled: boolean, frequency_days: number, done: string) =>
    onUpdate({ bucket: { category: bucket.category, enabled, frequency_days } }, done);
  const lagging = bucket.enabled && cycleNights !== null && cycleNights > bucket.frequency_days;

  return (
    <div className="rounded-xl border border-slate-200/70 p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: DESIGNATION_COLORS[bucket.category] ?? '#94a3b8' }}
          />
          <span className="text-xs font-semibold text-slate-800 truncate">{bucket.category}</span>
        </div>
        <Toggle
          checked={bucket.enabled}
          disabled={saving}
          label={bucket.enabled ? `Stop scheduled research for ${short}` : `Schedule research for ${short}`}
          onChange={(on) =>
            save(on, bucket.frequency_days, on ? `${short}: scheduled.` : `${short}: schedule off.`)
          }
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <span>
            {bucket.profiles.toLocaleString('en-IN')} alumni
            {bucket.enabled && bucket.due !== null && ` · ${bucket.due.toLocaleString('en-IN')} due now`}
          </span>
          <Link
            to={`/dashboard/research-changes?designations=${encodeURIComponent(bucket.category)}`}
            className="text-accent hover:underline font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            View details
          </Link>
        </div>
        <div
          className={`flex rounded-lg border border-slate-200/60 bg-slate-50 overflow-hidden ${
            bucket.enabled ? '' : 'opacity-60'
          }`}
        >
          {Object.entries(FREQUENCY_LABELS).map(([days, [label, title]]) => {
            const d = Number(days);
            return (
              <button
                key={days}
                disabled={saving}
                onClick={() =>
                  d !== bucket.frequency_days && save(bucket.enabled, d, `${short}: ${title.toLowerCase()}.`)
                }
                title={title}
                className={`px-2 py-1 text-[10px] font-semibold transition-colors disabled:cursor-not-allowed ${
                  bucket.frequency_days === d
                    ? 'bg-accent/10 text-accent'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {bucket.profiles === 0 && (
        <p className="text-[10px] text-slate-400">
          No classified alumni yet. Runs once VALS puts alumni in this bucket.
        </p>
      )}
      {lagging && (
        <p className="text-[10px] text-amber-700">
          Can't keep up {FREQUENCY_LABELS[bucket.frequency_days]?.[1].toLowerCase()} at {cap} per
          night; alumni here will be refreshed less often.
        </p>
      )}
      {bucket.last_run_at && (
        <div className="flex items-center gap-2 text-[10px] text-slate-400">
          Last run {formatTime(bucket.last_run_at)}
          {bucket.last_run_status && <StatusChip status={bucket.last_run_status} />}
        </div>
      )}
    </div>
  );
}

function TargetAskBucketRow({
  bucket,
  cycleNights,
  cap,
  saving,
  onUpdate,
}: {
  bucket: TargetAskScheduleBucket;
  cycleNights: number | null;
  cap: number;
  saving: boolean;
  onUpdate: ReturnType<typeof useResearchSchedule>['update'];
}) {
  const save = (enabled: boolean, frequency_days: number, done: string) =>
    onUpdate(
      {
        target_ask_bucket: {
          label: bucket.label,
          min: bucket.min,
          max: bucket.max,
          enabled,
          frequency_days,
        },
      },
      done
    );
  const lagging = bucket.enabled && cycleNights !== null && cycleNights > bucket.frequency_days;

  return (
    <div className="rounded-xl border border-slate-200/70 p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-slate-800 truncate">{bucket.label}</span>
        </div>
        <Toggle
          checked={bucket.enabled}
          disabled={saving}
          label={bucket.enabled ? `Stop scheduled research for ${bucket.label}` : `Schedule research for ${bucket.label}`}
          onChange={(on) =>
            save(on, bucket.frequency_days, on ? `${bucket.label}: scheduled.` : `${bucket.label}: schedule off.`)
          }
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <span>
            {bucket.profiles.toLocaleString('en-IN')} alumni
            {bucket.enabled && bucket.due !== null && ` · ${bucket.due.toLocaleString('en-IN')} due now`}
          </span>
          <Link
            to={`/dashboard/research-changes?target_asks=${encodeURIComponent(bucket.label)}`}
            className="text-accent hover:underline font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            View details
          </Link>
        </div>
        <div
          className={`flex rounded-lg border border-slate-200/60 bg-slate-50 overflow-hidden ${
            bucket.enabled ? '' : 'opacity-60'
          }`}
        >
          {Object.entries(FREQUENCY_LABELS).map(([days, [label, title]]) => {
            const d = Number(days);
            return (
              <button
                key={days}
                disabled={saving}
                onClick={() =>
                  d !== bucket.frequency_days && save(bucket.enabled, d, `${bucket.label}: ${title.toLowerCase()}.`)
                }
                title={title}
                className={`px-2 py-1 text-[10px] font-semibold transition-colors disabled:cursor-not-allowed ${
                  bucket.frequency_days === d
                    ? 'bg-accent/10 text-accent'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {lagging && (
        <p className="text-[10px] text-amber-700">
          Can't keep up {FREQUENCY_LABELS[bucket.frequency_days]?.[1].toLowerCase()} at {cap} per
          night; alumni here will be refreshed less often.
        </p>
      )}
      {bucket.last_run_at && (
        <div className="flex items-center gap-2 text-[10px] text-slate-400">
          Last run {formatTime(bucket.last_run_at)}
          {bucket.last_run_status && <StatusChip status={bucket.last_run_status} />}
        </div>
      )}
    </div>
  );
}


/**
 * Admin panel for nightly AI research per designation bucket. Opened from the
 * constellation's Designation view. Only AI research is run, never VALS, and
 * one profile at a time, so the six buckets share the free-tier budget.
 */
export function ResearchScheduleSheet({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { overview, runs, targetAskRanges, loading, saving, error, update } = useResearchSchedule(isOpen);
  const [selectedRangeIdx, setSelectedRangeIdx] = useState<string>('');

  return (
    <Sheet isOpen={isOpen} onClose={onClose} title="Scheduled AI research" maxWidth="max-w-lg">

      {loading && !overview ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : error && !overview ? (
        <p className="text-xs text-rose-600">{error}</p>
      ) : overview ? (
        <div className="space-y-5">
          <StatusCard overview={overview} saving={saving} onUpdate={update} />

          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Buckets</p>
            {overview.buckets.map((b) => (
              <BucketRow
                key={b.category}
                bucket={b}
                cycleNights={overview.cycle_nights}
                cap={overview.nightly_cap}
                saving={saving}
                onUpdate={update}
              />
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Target Ask Buckets</p>
            {overview.target_ask_buckets?.map((b) => (
              <TargetAskBucketRow
                key={b.label}
                bucket={b}
                cycleNights={overview.cycle_nights}
                cap={overview.nightly_cap}
                saving={saving}
                onUpdate={update}
              />
            ))}
            
            <div className="flex items-center gap-2 mt-3">
              <select
                className="text-xs border border-slate-200 rounded px-2 py-1 flex-1"
                value={selectedRangeIdx}
                onChange={(e) => setSelectedRangeIdx(e.target.value)}
              >
                <option value="">Select Target Ask Range to Schedule...</option>
                {targetAskRanges
                  .map((r, i) => ({ r, i }))
                  .filter(({ r }) => !overview.target_ask_buckets?.some((b) => b.label === r.label))
                  .map(({ r, i }) => (
                    <option key={i} value={i}>
                      {r.label}
                    </option>
                  ))}
              </select>
              <Button
                variant="outline"
                className="px-3 py-1 text-xs h-auto"
                disabled={!selectedRangeIdx || saving}
                onClick={() => {
                  const range = targetAskRanges[Number(selectedRangeIdx)];
                  if (range) {
                    update({
                      target_ask_bucket: {
                        label: range.label,
                        min: range.min,
                        max: range.max,
                        enabled: true,
                        frequency_days: 30, // Default frequency
                      },
                    }, `${range.label}: scheduled.`);
                    setSelectedRangeIdx('');
                  }
                }}
              >
                Add
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Recent runs
            </p>
            {runs.length === 0 ? (
              <p className="text-[10px] text-slate-400">No scheduled runs yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200/70">
                {runs.map((r, i) => (
                  <li key={`${r.profile_id}-${r.started_at}-${i}`} className="px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium text-slate-700 truncate">
                        {r.name || r.profile_id}
                      </span>
                      <StatusChip status={r.status} />
                    </div>
                    <p className="text-[10px] text-slate-400">
                      {DESIGNATION_SHORT[r.bucket] ?? r.bucket} · {formatTime(r.started_at)}
                    </p>
                    {r.error && (
                      <p className="text-[10px] text-rose-500 mt-0.5 break-words">{r.error}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}
